/**
 * Population evaluation engine
 * Calculates expected spawns for blueprints in populations
 */

import type { PopulationData, PopulationItem } from '@/qud/types.js';
import type { EvaluationContext } from './types.js';

/**
 * Parse a dice expression and return expected value
 * Supports: NdM, X-Y ranges, constants, + for addition
 */
export function expectedDiceValue(dice: string): number {
  if (!dice) return 1;

  // Handle addition
  if (dice.includes('+')) {
    const parts = dice.split('+');
    return parts.reduce((sum, part) => sum + expectedDiceValue(part.trim()), 0);
  }

  // Handle NdM dice notation
  if (dice.includes('d')) {
    const [numStr, sidesStr] = dice.split('d');
    const num = parseInt(numStr, 10) || 1;
    const sides = parseInt(sidesStr, 10);
    if (isNaN(sides)) return num;
    // E[NdM] = N * (M + 1) / 2
    return (num * (sides + 1)) / 2;
  }

  // Handle X-Y range
  if (dice.includes('-')) {
    const [minStr, maxStr] = dice.split('-');
    const min = parseInt(minStr, 10);
    const max = parseInt(maxStr, 10);
    if (isNaN(min) || isNaN(max)) return 1;
    // E[X-Y] = (X + Y) / 2
    return (min + max) / 2;
  }

  // Constant
  const val = parseInt(dice, 10);
  return isNaN(val) ? 1 : val;
}

/**
 * Parse a chance string and return probability
 * Handles comma-separated chances like "15,15,15" (three independent 15% rolls)
 */
export function parseChance(chance: string): number {
  if (!chance) return 1;

  // Handle comma-separated chances (independent rolls)
  if (chance.includes(',')) {
    const parts = chance.split(',').map((p) => parseInt(p.trim(), 10) / 100);
    // P(at least one success) = 1 - P(all fail)
    const allFail = parts.reduce((prod, p) => prod * (1 - p), 1);
    return 1 - allFail;
  }

  // Single percentage
  const pct = parseInt(chance, 10);
  return isNaN(pct) ? 1 : pct / 100;
}

/**
 * Resolve parameterized table name
 * Handles {zonetier}, {zonetier+N}, {zonetier-N}
 */
export function resolveTableName(name: string, context: EvaluationContext): string {
  return name.replace(/\{zonetier([+-]\d+)?\}/g, (_match, offset) => {
    let tier = context.zonetier;
    if (offset) {
      tier += parseInt(offset, 10);
    }
    // Clamp to 0-8
    tier = Math.max(0, Math.min(8, tier));
    return tier.toString();
  });
}

/**
 * Calculate zone tier from stratum level and terrain's RegionTier
 */
export function calculateZoneTier(stratum: number, terrainRegionTier: number): number {
  // Surface: use terrain's RegionTier
  if (stratum <= 15) {
    return terrainRegionTier;
  }
  // Underground: tier = (Z - 16) / 5 + 2, clamped 1-8
  const tier = Math.floor((stratum - 16) / 5) + 2;
  return Math.max(1, Math.min(8, tier));
}

/**
 * Population evaluator class
 */
export class PopulationEvaluator {
  private populations: PopulationData;
  private visitedTables: Set<string> = new Set(); // Cycle detection
  // Memoization cache: "populationName|blueprintName|zonetier" -> expected spawns
  private spawnCache: Map<string, number> = new Map();

  constructor(populations: PopulationData) {
    this.populations = populations;
  }

  /**
   * Clear the spawn cache (call when switching to a new blueprint search)
   */
  clearCache(): void {
    this.spawnCache.clear();
  }

  /**
   * Find expected spawns of a blueprint in a population
   * Returns 0 if blueprint not found in population
   */
  findBlueprintExpectedSpawns(
    populationName: string,
    blueprintName: string,
    context: EvaluationContext
  ): number {
    // Check memoization cache
    const cacheKey = `${populationName}|${blueprintName}|${context.zonetier}`;
    const cached = this.spawnCache.get(cacheKey);
    if (cached !== undefined) {
      return cached;
    }

    const resolvedName = resolveTableName(populationName, context);
    const population = this.populations.populations[resolvedName];
    if (!population) {
      this.spawnCache.set(cacheKey, 0);
      return 0;
    }

    // Reset cycle detection for new search
    this.visitedTables.clear();

    const result = this.evaluateItemsForBlueprint(population.items, blueprintName, context, 1);
    this.spawnCache.set(cacheKey, result);
    return result;
  }

  /**
   * Evaluate items recursively to find expected spawns of a blueprint
   */
  private evaluateItemsForBlueprint(
    items: PopulationItem[],
    blueprintName: string,
    context: EvaluationContext,
    parentMultiplier: number
  ): number {
    let totalExpected = 0;

    // Top-level population items use pickeach behavior (all items evaluated)
    for (const item of items) {
      const itemChance = parseChance(item.chance ?? '');
      const itemNumber = expectedDiceValue(item.number ?? '1');
      const itemMultiplier = parentMultiplier * itemChance * itemNumber;

      if (item.type === 'object') {
        if (item.blueprint === blueprintName) {
          // Found the blueprint
          totalExpected += itemMultiplier;
        }
      } else if (item.type === 'table') {
        // Recurse into referenced table
        const resolvedTableName = resolveTableName(item.name, context);

        // Cycle detection
        if (this.visitedTables.has(resolvedTableName)) {
          continue;
        }
        this.visitedTables.add(resolvedTableName);

        const table = this.populations.populations[resolvedTableName];
        if (table) {
          totalExpected += this.evaluateItemsForBlueprint(
            table.items,
            blueprintName,
            context,
            itemMultiplier
          );
        }

        this.visitedTables.delete(resolvedTableName);
      } else if (item.type === 'group') {
        // Evaluate group based on style
        const groupMultiplier = itemMultiplier;
        totalExpected += this.evaluateGroupForBlueprint(
          item,
          blueprintName,
          context,
          groupMultiplier,
          item.style
        );
      }
    }

    return totalExpected;
  }

  /**
   * Evaluate a group for expected spawns of a blueprint
   */
  private evaluateGroupForBlueprint(
    group: { style: 'pickone' | 'pickeach'; items: PopulationItem[] },
    blueprintName: string,
    context: EvaluationContext,
    parentMultiplier: number,
    style: 'pickone' | 'pickeach'
  ): number {
    let totalExpected = 0;

    if (style === 'pickeach') {
      // Each item is evaluated independently
      for (const item of group.items) {
        const itemChance = parseChance(item.chance ?? '');
        const itemNumber = expectedDiceValue(item.number ?? '1');
        const itemMultiplier = parentMultiplier * itemChance * itemNumber;

        if (item.type === 'object') {
          if (item.blueprint === blueprintName) {
            totalExpected += itemMultiplier;
          }
        } else if (item.type === 'table') {
          const resolvedTableName = resolveTableName(item.name, context);
          if (!this.visitedTables.has(resolvedTableName)) {
            this.visitedTables.add(resolvedTableName);
            const table = this.populations.populations[resolvedTableName];
            if (table) {
              totalExpected += this.evaluateItemsForBlueprint(
                table.items,
                blueprintName,
                context,
                itemMultiplier
              );
            }
            this.visitedTables.delete(resolvedTableName);
          }
        } else if (item.type === 'group') {
          const groupChance = parseChance(item.chance ?? '');
          const groupNumber = expectedDiceValue(item.number ?? '1');
          totalExpected += this.evaluateGroupForBlueprint(
            item,
            blueprintName,
            context,
            itemMultiplier * groupChance * groupNumber,
            item.style
          );
        }
      }
    } else {
      // pickone: one item is selected based on weight
      const totalWeight = group.items.reduce((sum, item) => sum + (item.weight ?? 1), 0);

      for (const item of group.items) {
        const itemWeight = item.weight ?? 1;
        const selectionProb = itemWeight / totalWeight;
        const itemChance = parseChance(item.chance ?? '');
        const itemNumber = expectedDiceValue(item.number ?? '1');
        const itemMultiplier = parentMultiplier * selectionProb * itemChance * itemNumber;

        if (item.type === 'object') {
          if (item.blueprint === blueprintName) {
            totalExpected += itemMultiplier;
          }
        } else if (item.type === 'table') {
          const resolvedTableName = resolveTableName(item.name, context);
          if (!this.visitedTables.has(resolvedTableName)) {
            this.visitedTables.add(resolvedTableName);
            const table = this.populations.populations[resolvedTableName];
            if (table) {
              totalExpected += this.evaluateItemsForBlueprint(
                table.items,
                blueprintName,
                context,
                itemMultiplier
              );
            }
            this.visitedTables.delete(resolvedTableName);
          }
        } else if (item.type === 'group') {
          totalExpected += this.evaluateGroupForBlueprint(
            item,
            blueprintName,
            context,
            itemMultiplier,
            item.style
          );
        }
      }
    }

    return totalExpected;
  }

  /**
   * Get all blueprints referenced in a population (for indexing)
   */
  getAllBlueprintsInPopulation(populationName: string, context: EvaluationContext): Set<string> {
    const blueprints = new Set<string>();
    const resolvedName = resolveTableName(populationName, context);
    const population = this.populations.populations[resolvedName];
    if (!population) return blueprints;

    this.visitedTables.clear();
    this.collectBlueprints(population.items, context, blueprints);
    return blueprints;
  }

  private collectBlueprints(
    items: PopulationItem[],
    context: EvaluationContext,
    blueprints: Set<string>
  ): void {
    for (const item of items) {
      if (item.type === 'object') {
        blueprints.add(item.blueprint);
      } else if (item.type === 'table') {
        const resolvedTableName = resolveTableName(item.name, context);
        if (!this.visitedTables.has(resolvedTableName)) {
          this.visitedTables.add(resolvedTableName);
          const table = this.populations.populations[resolvedTableName];
          if (table) {
            this.collectBlueprints(table.items, context, blueprints);
          }
          this.visitedTables.delete(resolvedTableName);
        }
      } else if (item.type === 'group') {
        this.collectBlueprints(item.items, context, blueprints);
      }
    }
  }
}
